import React from "react";
import { Box, Group, Text, Tooltip } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import styles from "./StatStrip.module.css";

/** Width of a stat's info tooltip (as shipped on the Sales strip). */
const INFO_TOOLTIP_WIDTH = 260;

export interface StatStripStat {
  label: string;
  value: string;
  /** Stable React key when two stats could share a label. Defaults to label. */
  key?: string;
  /** Render the value in danger red (e.g. non-zero refunds). */
  danger?: boolean;
  /**
   * Optional help text. When present, an info icon is rendered beside the label
   * and reveals this content in a tooltip on hover / focus / tap. Callers that
   * omit it keep the plain label markup unchanged.
   */
  info?: React.ReactNode;
}

export interface StatStripProps {
  stats: StatStripStat[];
  /**
   * "rules" (default): rule-separated row with no card (Sales, Client Groups,
   * Campaigns). "card": a bordered card with one equal column per stat (the
   * Reports KPI totals, 1–4 stats).
   */
  variant?: "rules" | "card";
}

/**
 * Headline stat row (moved from sales/shared/SummaryStrip, which re-exports
 * it). Callers recompute every stat from the FILTERED data, so narrowing a
 * filter changes the numbers. The "rules" variant wraps to a second line
 * rather than clipping (layout floor rule 3). A stat may carry optional `info`
 * help text, surfaced through an info icon + tooltip beside its label.
 */
export default function StatStrip({
  stats,
  variant = "rules",
}: StatStripProps) {
  const isCard = variant === "card";
  return (
    <Box
      className={
        isCard ? `${styles.summaryStrip} ${styles.card}` : styles.summaryStrip
      }
      style={
        isCard
          ? ({
              "--stat-strip-columns": Math.max(1, stats.length),
            } as React.CSSProperties)
          : undefined
      }
    >
      {stats.map((stat) => (
        <Box key={stat.key ?? stat.label} className={styles.summaryStat}>
          {stat.info ? (
            <Group
              className={styles.summaryLabelRow}
              gap={4}
              wrap="nowrap"
              align="center"
            >
              <Text className={styles.summaryLabel}>{stat.label}</Text>
              <Tooltip
                label={stat.info}
                multiline
                w={INFO_TOOLTIP_WIDTH}
                withArrow
                events={{ hover: true, focus: true, touch: true }}
              >
                <IconInfoCircle
                  size="0.85rem"
                  className={styles.summaryInfoIcon}
                  aria-hidden
                />
              </Tooltip>
            </Group>
          ) : (
            <Text className={styles.summaryLabel}>{stat.label}</Text>
          )}
          <Text
            className={`${styles.summaryValue} ${
              stat.danger ? styles.summaryValueDanger : ""
            }`}
          >
            {stat.value}
          </Text>
        </Box>
      ))}
    </Box>
  );
}
